import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';

const AdminDashboardPage = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalBooks: 0,
    activeUsers: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate fetching data
    setTimeout(() => {
      // Mock data
      setStats({
        totalUsers: 1250,
        totalBooks: 342,
        activeUsers: 876
      });
      
      setRecentActivity([
        { id: 1, type: 'user_registered', user: 'John Doe', timestamp: '2023-03-15T10:30:00Z' },
        { id: 2, type: 'book_added', title: 'The Great Gatsby', admin: 'Admin User', timestamp: '2023-03-15T09:45:00Z' },
        { id: 3, type: 'user_registered', user: 'Robert Johnson', timestamp: '2023-03-14T16:15:00Z' },
        { id: 4, type: 'book_added', title: '1984', admin: 'Admin User', timestamp: '2023-03-14T14:30:00Z' }
      ]);
      
      setLoading(false);
    }, 1000);
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'user_registered':
        return '👤';
      case 'book_added':
        return '📚';
      default:
        return '📝';
    }
  };

  const getActivityText = (activity) => {
    switch (activity.type) {
      case 'user_registered':
        return `${activity.user} registered as a new user`;
      case 'book_added':
        return `${activity.admin} added "${activity.title}" to the library`;
      default:
        return 'Unknown activity';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-light py-12">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-800">Admin Dashboard</h1>
            <p className="text-gray-600">Manage your Page4Change platform</p>
          </div>

          {/* Stats Section */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-10">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <Card className="text-center p-6">
                <h3 className="text-lg font-semibold text-gray-700">Total Users</h3>
                <p className="text-4xl font-bold text-primary mt-2">{stats.totalUsers}</p>
              </Card>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              <Card className="text-center p-6">
                <h3 className="text-lg font-semibold text-gray-700">Total Books</h3>
                <p className="text-4xl font-bold text-primary mt-2">{stats.totalBooks}</p>
              </Card>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: 0.3 }}
            >
              <Card className="text-center p-6">
                <h3 className="text-lg font-semibold text-gray-700">Active Users</h3>
                <p className="text-4xl font-bold text-secondary-green mt-2">{stats.activeUsers}</p>
              </Card>
            </motion.div>
          </div>

          {/* Quick Actions */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Quick Actions</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Link to="/admin/books">
                <Card className="p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-center">
                    <div className="bg-primary bg-opacity-10 p-4 rounded-full mr-4">
                      <span className="text-2xl">📚</span>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-800">Manage Books</h3>
                      <p className="text-sm text-gray-600">Add, edit, or remove books from the library</p>
                    </div>
                  </div>
                </Card>
              </Link>
              
              <Link to="/admin/users">
                <Card className="p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-center">
                    <div className="bg-secondary-green bg-opacity-10 p-4 rounded-full mr-4">
                      <span className="text-2xl">👥</span>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-800">Manage Users</h3>
                      <p className="text-sm text-gray-600">View and manage user accounts and roles</p>
                    </div>
                  </div>
                </Card>
              </Link>
              
              <Link to="/admin/reports">
                <Card className="p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-center">
                    <div className="bg-secondary-orange bg-opacity-10 p-4 rounded-full mr-4">
                      <span className="text-2xl">📊</span>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-800">View Reports</h3>
                      <p className="text-sm text-gray-600">Access analytics and usage reports</p>
                    </div>
                  </div>
                </Card>
              </Link>
            </div>
          </div>

          {/* Recent Activity */}
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Recent Activity</h2>
            <Card className="p-6">
              {recentActivity.length > 0 ? (
                <div className="divide-y divide-gray-200">
                  {recentActivity.map((activity, index) => (
                    <motion.div
                      key={activity.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                      className="py-4 first:pt-0 last:pb-0"
                    >
                      <div className="flex items-start">
                        <div className="bg-gray-100 p-3 rounded-full mr-4">
                          <span className="text-xl">{getActivityIcon(activity.type)}</span>
                        </div>
                        <div className="flex-grow">
                          <p className="text-gray-800">{getActivityText(activity)}</p>
                          <p className="text-xs text-gray-500 mt-1">{formatDate(activity.timestamp)}</p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-600 py-4">No recent activity to display</p>
              )}
              
              <div className="mt-6 text-center">
                <Button variant="secondary">View All Activity</Button>
              </div>
            </Card>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminDashboardPage; 